import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { ColumnDef, HeaderContext } from "@tanstack/react-table";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  DataTable,
  DataTableColumnHeader,
  DatePicker,
  IconButton,
  SearchInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tag,
} from "@jem-open/jem-ui";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { showNotification } from "@mantine/notifications";
import { buildResolveBody } from "../utils/resolveExceptionBody";
import PageContainer from "../../../themes/components/PageContainer";
import AccessGuard from "../../../common/uac/AccessGuard";
import useExceptions from "../hooks/useExceptions";
import { AttendanceException } from "../services/model/AttendanceException";
import {
  exceptionTypeLabels,
  exceptionTypeVariants,
  isNewExceptionType,
  NewExceptionType,
} from "../constants/exceptionTypeConfig";
import ExceptionActionModalRouter from "../components/ExceptionActionModalRouter";

const formatDate = (dateStr: string): string =>
  dayjs(dateStr).format("D MMM YYYY");

type TypeFilterValue = "all" | NewExceptionType;

const DateCell: React.FC<{ value: string; muted?: boolean }> = ({
  value,
  muted,
}) => (
  <span
    className={
      muted
        ? "text-sm text-greyscale-text-caption"
        : "text-sm text-greyscale-text-body"
    }
  >
    {formatDate(value)}
  </span>
);

const EmployeeCell: React.FC<{ value: string; muted?: boolean }> = ({
  value,
  muted,
}) =>
  muted ? (
    <span className="text-sm text-greyscale-text-caption">{value}</span>
  ) : (
    <span className="text-sm font-semibold text-greyscale-text-title">
      {value}
    </span>
  );

const UnresolvedTypeCell: React.FC<{
  type: AttendanceException["exception_type"];
}> = ({ type }) => {
  if (!isNewExceptionType(type)) {
    return (
      <Tag variant="drafted" size="sm">
        {type}
      </Tag>
    );
  }
  return (
    <Tag variant={exceptionTypeVariants[type]} size="sm">
      {exceptionTypeLabels[type]}
    </Tag>
  );
};

const ResolvedTypeCell: React.FC<{
  type: AttendanceException["exception_type"];
}> = ({ type }) => {
  const label = isNewExceptionType(type) ? exceptionTypeLabels[type] : type;
  return (
    <Tag variant="success" size="sm">
      {label}
    </Tag>
  );
};

const TextCell: React.FC<{ value: string | null | undefined }> = ({
  value,
}) => (
  <span className="text-sm text-greyscale-text-caption">{value ?? ""}</span>
);

const ColumnHeader: React.FC<{
  column: HeaderContext<AttendanceException, unknown>["column"];
  title: string;
}> = ({ column, title }) => (
  <DataTableColumnHeader column={column} title={title} />
);

const ActionHeader: React.FC = () => (
  <span className="text-center block">Action</span>
);

const ResolveActionCell: React.FC<{
  row: AttendanceException;
  onClick: (row: AttendanceException) => void;
}> = ({ row, onClick }) => (
  <div className="flex justify-center">
    <Button variant="outline" size="sm" onClick={() => onClick(row)}>
      Resolve
    </Button>
  </div>
);

const ReopenActionCell: React.FC<{
  row: AttendanceException;
  onClick: (id: string) => void;
}> = ({ row, onClick }) => (
  <div className="flex justify-center">
    <Button variant="outline" size="sm" onClick={() => onClick(row.id)}>
      Reopen
    </Button>
  </div>
);

const buildUnresolvedColumns = (
  onResolveClick: (row: AttendanceException) => void
): ColumnDef<AttendanceException>[] => [
  {
    accessorKey: "exception_date",
    header: ({ column }) => <ColumnHeader column={column} title="Date" />,
    cell: ({ row }) => <DateCell value={row.original.exception_date} />,
  },
  {
    accessorKey: "employee_name",
    header: ({ column }) => <ColumnHeader column={column} title="Employee" />,
    cell: ({ row }) => <EmployeeCell value={row.original.employee_name} />,
  },
  {
    accessorKey: "exception_type",
    header: ({ column }) => <ColumnHeader column={column} title="Type" />,
    cell: ({ row }) => (
      <UnresolvedTypeCell type={row.original.exception_type} />
    ),
  },
  {
    accessorKey: "location_name",
    header: ({ column }) => <ColumnHeader column={column} title="Site" />,
  },
  {
    accessorKey: "region_name",
    header: ({ column }) => <ColumnHeader column={column} title="Region" />,
  },
  {
    accessorKey: "timestamp",
    header: ({ column }) => <ColumnHeader column={column} title="Time" />,
    cell: ({ row }) => <TextCell value={row.original.timestamp} />,
  },
  {
    id: "actions",
    header: () => <ActionHeader />,
    cell: ({ row }) => (
      <ResolveActionCell row={row.original} onClick={onResolveClick} />
    ),
  },
];

const buildResolvedColumns = (
  onReopen: (id: string) => void
): ColumnDef<AttendanceException>[] => [
  {
    accessorKey: "exception_date",
    header: ({ column }) => <ColumnHeader column={column} title="Date" />,
    cell: ({ row }) => <DateCell value={row.original.exception_date} muted />,
  },
  {
    accessorKey: "employee_name",
    header: ({ column }) => <ColumnHeader column={column} title="Employee" />,
    cell: ({ row }) => (
      <EmployeeCell value={row.original.employee_name} muted />
    ),
  },
  {
    accessorKey: "exception_type",
    header: ({ column }) => <ColumnHeader column={column} title="Type" />,
    cell: ({ row }) => <ResolvedTypeCell type={row.original.exception_type} />,
  },
  {
    accessorKey: "location_name",
    header: ({ column }) => <ColumnHeader column={column} title="Site" />,
    cell: ({ row }) => <TextCell value={row.original.location_name} />,
  },
  {
    accessorKey: "region_name",
    header: ({ column }) => <ColumnHeader column={column} title="Region" />,
    cell: ({ row }) => <TextCell value={row.original.region_name} />,
  },
  {
    accessorKey: "timestamp",
    header: ({ column }) => <ColumnHeader column={column} title="Time" />,
    cell: ({ row }) => <TextCell value={row.original.timestamp} />,
  },
  {
    id: "actions",
    header: () => <ActionHeader />,
    cell: ({ row }) => (
      <ReopenActionCell row={row.original} onClick={onReopen} />
    ),
  },
];

const formatCardDate = (dateStr: string): string =>
  dayjs(dateStr).format("D MMM YYYY");

const formatCardTime = (raw: string): string => {
  if (!raw) return "";
  if (raw.includes("T") || raw.includes(" ")) {
    const parsed = dayjs(raw);
    return parsed.isValid() ? parsed.format("h:mma") : raw;
  }
  return raw;
};

interface ExceptionCardProps {
  exception: AttendanceException;
  variant: "unresolved" | "resolved";
  onResolveClick: (row: AttendanceException) => void;
  onReopen: (id: string) => void;
}

const ExceptionCard: React.FC<ExceptionCardProps> = ({
  exception,
  variant,
  onResolveClick,
  onReopen,
}) => {
  const isResolved = variant === "resolved";
  const type = exception.exception_type;
  const tagLabel = isNewExceptionType(type) ? exceptionTypeLabels[type] : type;
  const tagVariant = (() => {
    if (isResolved) return "success" as const;
    if (isNewExceptionType(type)) return exceptionTypeVariants[type];
    return "drafted" as const;
  })();

  return (
    <div className="rounded-lg border border-greyscale-border bg-white p-sm">
      <div className="flex items-start justify-between gap-xs">
        <div className="text-xs text-greyscale-text-caption">
          {formatCardDate(exception.exception_date)}
          {exception.timestamp
            ? ` · ${formatCardTime(exception.timestamp)}`
            : ""}
        </div>
        <Tag variant={tagVariant} size="sm">
          {tagLabel}
        </Tag>
      </div>
      <p
        className={
          isResolved
            ? "mt-xxs text-sm font-semibold text-greyscale-text-caption"
            : "mt-xxs text-sm font-semibold text-greyscale-text-title"
        }
      >
        {exception.employee_name}
      </p>
      <p className="text-xs text-greyscale-text-caption mt-xxxs">
        {exception.location_name}
        {exception.region_name ? ` · ${exception.region_name}` : ""}
      </p>
      <div className="mt-sm flex justify-end">
        {isResolved ? (
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0 gap-xxs text-sm font-medium text-greyscale-text-title no-underline hover:no-underline"
            onClick={() => onReopen(exception.id)}
          >
            Reopen
            <ChevronRight size={14} aria-hidden="true" />
          </Button>
        ) : (
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0 gap-xxs text-sm font-medium text-greyscale-text-title no-underline hover:no-underline"
            onClick={() => onResolveClick(exception)}
          >
            Resolve
            <ChevronRight size={14} aria-hidden="true" />
          </Button>
        )}
      </div>
    </div>
  );
};

const MOBILE_PAGE_SIZE = 10;

interface MobilePaginationProps {
  page: number;
  total: number;
  pageSize: number;
  onPageChange: (next: number) => void;
}

const MobilePagination: React.FC<MobilePaginationProps> = ({
  page,
  total,
  pageSize,
  onPageChange,
}) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (totalPages <= 1) return null;
  const startIdx = (page - 1) * pageSize;
  const endIdx = Math.min(startIdx + pageSize, total);
  return (
    <div className="sm:hidden flex items-center justify-between gap-sm mt-md">
      <span className="text-sm text-greyscale-text-caption">
        {startIdx + 1}-{endIdx} of {total}
      </span>
      <div className="flex gap-xs">
        <IconButton
          variant="ghost"
          size="sm"
          disabled={page === 1}
          onClick={() => onPageChange(page - 1)}
          aria-label="Previous page"
          className="h-11 w-11"
        >
          <ChevronLeft size={16} />
        </IconButton>
        <IconButton
          variant="ghost"
          size="sm"
          disabled={page === totalPages}
          onClick={() => onPageChange(page + 1)}
          aria-label="Next page"
          className="h-11 w-11"
        >
          <ChevronRight size={16} />
        </IconButton>
      </div>
    </div>
  );
};

const ExceptionsPageInner: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const siteId = searchParams.get("site") || undefined;
  // shift_site scopes the list to a whole ShiftSite (all its child locations);
  // site_name is passed by the shift-site page so we can label without a lookup.
  const shiftSiteId = searchParams.get("shift_site") || undefined;
  const siteNameParam = searchParams.get("site_name") || undefined;

  const {
    filteredUnresolved,
    resolved,
    unresolvedCount,
    countByType,
    siteOptions,
    typeFilter,
    setTypeFilter,
    siteFilter,
    setSiteFilter,
    dateFilter,
    setDateFilter,
    searchQuery,
    setSearchQuery,
    selectedException,
    setSelectedException,
    handleResolve,
    handleReopen,
  } = useExceptions(siteId, shiftSiteId);

  // A location filter (?site=) resolves its name from the locations list; a
  // site filter (?shift_site=) carries its name in the URL.
  const isScopedToSite = Boolean(siteId || shiftSiteId);
  const siteName = React.useMemo(() => {
    if (siteNameParam) return siteNameParam;
    if (!siteId) return undefined;
    const matched = siteOptions.find((s) => s.id === siteId);
    return matched?.name;
  }, [siteNameParam, siteId, siteOptions]);

  const onResolveFromModal = useCallback(
    (id: string, action: string, reason?: string) => {
      const target = selectedException;
      const exceptionType = target?.exception_type ?? "no_show";

      // "Find Replacement" (no_show) and "Replaced" (no_data) hand off to the
      // existing replacement flow. Defer the resolve until the override is
      // actually created — if the resolver cancels there, the exception stays open.
      const triggersReplacementFlow =
        (exceptionType === "no_show" && action === "replacement") ||
        (exceptionType === "no_data" && action === "replaced");
      if (triggersReplacementFlow && target) {
        const params = new URLSearchParams({
          tab: "replacement",
          exception_id: id,
          exception_action: action,
        });
        navigate(
          `/time-attendance/${target.shift_location}/employee/${
            target.employee
          }?${params.toString()}`
        );
        return;
      }

      const body = buildResolveBody(exceptionType, action, reason);
      if (body === null) {
        showNotification({
          title: "Invalid input",
          message:
            "We couldn't read the confirmed clock time. Please re-enter the time and try again.",
          color: "red",
        });
        return;
      }
      handleResolve(id, body);
    },
    [handleResolve, navigate, selectedException]
  );

  const handleBack = (): void => {
    if (isScopedToSite) {
      navigate(-1);
    } else {
      navigate("/time-attendance");
    }
  };

  const unresolvedColumns = React.useMemo(
    () => buildUnresolvedColumns(setSelectedException),
    [setSelectedException]
  );

  const resolvedColumns = React.useMemo(
    () => buildResolvedColumns(handleReopen),
    [handleReopen]
  );

  const [unresolvedMobilePage, setUnresolvedMobilePage] = useState(1);
  const [resolvedMobilePage, setResolvedMobilePage] = useState(1);

  // Reset to page 1 whenever filters or counts shrink.
  useEffect(() => {
    setUnresolvedMobilePage(1);
  }, [
    typeFilter,
    siteFilter,
    dateFilter,
    searchQuery,
    filteredUnresolved.length,
  ]);
  useEffect(() => {
    setResolvedMobilePage(1);
  }, [resolved.length]);

  const unresolvedMobileSlice = React.useMemo(() => {
    const start = (unresolvedMobilePage - 1) * MOBILE_PAGE_SIZE;
    return filteredUnresolved.slice(start, start + MOBILE_PAGE_SIZE);
  }, [filteredUnresolved, unresolvedMobilePage]);

  const resolvedMobileSlice = React.useMemo(() => {
    const start = (resolvedMobilePage - 1) * MOBILE_PAGE_SIZE;
    return resolved.slice(start, start + MOBILE_PAGE_SIZE);
  }, [resolved, resolvedMobilePage]);

  return (
    <PageContainer>
      <div className="pb-xl">
        <Breadcrumb className="mb-md">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleBack();
                }}
              >
                {siteName || "Time & Attendance"}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Exceptions</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex flex-col gap-sm mb-md sm:flex-row sm:items-center sm:justify-between sm:gap-md">
          <div>
            <h2 className="font-heading text-xl font-semibold text-greyscale-text-title flex items-center gap-xs">
              <AlertTriangle size={20} className="text-amber-500" />
              {siteName ? `${siteName} — Exceptions` : "Exceptions"}
              <Tag
                variant="failed"
                size="sm"
                aria-label={`${unresolvedCount} unresolved exceptions`}
              >
                {unresolvedCount}
              </Tag>
            </h2>
            <p className="text-sm text-greyscale-text-body mt-xxxs">
              {siteName
                ? `Resolve attendance exceptions for ${siteName}.`
                : "Resolve attendance exceptions across all sites."}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={handleBack}
            className="w-full sm:w-auto"
          >
            {siteName ? `Back to ${siteName}` : "Back to Overview"}
          </Button>
        </div>

        <div className="flex flex-col gap-xs mb-lg sm:flex-row sm:items-center sm:justify-between sm:gap-xxs">
          <div className="flex flex-col gap-xs w-full sm:flex-row sm:items-center sm:gap-xxs sm:w-auto">
            <DatePicker
              date={dateFilter}
              onDateChange={setDateFilter}
              placeholder="Filter by date"
              className="w-full sm:w-auto"
            />

            <Select
              value={typeFilter}
              onValueChange={(v) => setTypeFilter(v as TypeFilterValue)}
            >
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="All Exception Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Exception Types</SelectItem>
                <SelectItem value="no_show">No Show</SelectItem>
                <SelectItem value="supervisor_clock">
                  Supervisor Clock-In
                </SelectItem>
                <SelectItem value="no_data">No Data</SelectItem>
                <SelectItem value="geofence">Geofence Violation</SelectItem>
              </SelectContent>
            </Select>

            {!isScopedToSite && (
              <Select value={siteFilter} onValueChange={setSiteFilter}>
                <SelectTrigger className="w-full sm:w-[200px]">
                  <SelectValue placeholder="All Sites" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sites</SelectItem>
                  {siteOptions.map((site) => (
                    <SelectItem key={site.id} value={site.id}>
                      {site.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <SearchInput
            placeholder="Search employee, site, region"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClear={() => setSearchQuery("")}
            className="w-full sm:w-[280px]"
          />
        </div>

        <div className="flex flex-wrap items-center gap-xxs mb-xs sm:justify-end">
          <Tag variant="failed" size="sm">
            No Shows: {countByType("no_show")}
          </Tag>
          <Tag variant="processing" size="sm">
            Supervisor Clock-Ins: {countByType("supervisor_clock")}
          </Tag>
          <Tag variant="drafted" size="sm">
            No Data: {countByType("no_data")}
          </Tag>
          <Tag variant="failed" size="sm">
            Geofence: {countByType("geofence")}
          </Tag>
        </div>

        {/* Mobile card list — sm:hidden */}
        <div className="sm:hidden flex flex-col gap-sm">
          {unresolvedMobileSlice.map((row) => (
            <ExceptionCard
              key={row.id}
              exception={row}
              variant="unresolved"
              onResolveClick={setSelectedException}
              onReopen={handleReopen}
            />
          ))}
          {filteredUnresolved.length === 0 && (
            <p className="text-center text-sm text-greyscale-text-caption py-md">
              No unresolved exceptions match your filters.
            </p>
          )}
        </div>
        <MobilePagination
          page={unresolvedMobilePage}
          total={filteredUnresolved.length}
          pageSize={MOBILE_PAGE_SIZE}
          onPageChange={setUnresolvedMobilePage}
        />

        {/* Desktop table — hidden sm:block */}
        <div className="hidden sm:block">
          <DataTable
            columns={unresolvedColumns}
            data={filteredUnresolved}
            showPagination
            showToolbar={false}
          />
          {filteredUnresolved.length === 0 && (
            <p className="text-center text-sm text-greyscale-text-caption py-md">
              No unresolved exceptions match your filters.
            </p>
          )}
        </div>

        {resolved.length > 0 && (
          <div className="mt-lg">
            <h3 className="font-heading text-base font-semibold text-greyscale-text-title flex items-center gap-xs mb-sm">
              <CheckCircle2 size={16} className="text-emerald-500" />
              Resolved ({resolved.length})
            </h3>

            {/* Mobile resolved card list */}
            <div className="sm:hidden flex flex-col gap-sm opacity-75">
              {resolvedMobileSlice.map((row) => (
                <ExceptionCard
                  key={row.id}
                  exception={row}
                  variant="resolved"
                  onResolveClick={setSelectedException}
                  onReopen={handleReopen}
                />
              ))}
            </div>
            <MobilePagination
              page={resolvedMobilePage}
              total={resolved.length}
              pageSize={MOBILE_PAGE_SIZE}
              onPageChange={setResolvedMobilePage}
            />

            {/* Desktop resolved table */}
            <div className="hidden sm:block opacity-75">
              <DataTable
                columns={resolvedColumns}
                data={resolved}
                showPagination
                showToolbar={false}
              />
            </div>
          </div>
        )}

        <ExceptionActionModalRouter
          selectedException={selectedException}
          onClose={() => setSelectedException(null)}
          onResolve={onResolveFromModal}
        />
      </div>
    </PageContainer>
  );
};

const ExceptionsPage: React.FC = () => (
  <AccessGuard>
    <ExceptionsPageInner />
  </AccessGuard>
);

export default ExceptionsPage;
